/**
 * <s-review-form> — Sends a product review to Judge.me so it lands in the merchant's
 * Judge.me moderation queue, without depending on the Judge.me storefront script.
 *
 * Attributes (on <s-review-form>):
 *   data-endpoint        — Judge.me review submission URL (written by Liquid).
 *   data-success-message — i18n text shown after a successful submission.
 *   data-error-message   — i18n text shown when the submission fails.
 *   data-rating-message  — i18n text shown when no star rating is chosen.
 *
 * Required child markup:
 *   form              — Holds the inputs; named fields are sent as-is (name, email, rating, title, body,
 *                       plus hidden id, url, shop_domain, platform).
 *   [data-status]     — Live region for success and error text.
 *   button[type=submit]
 */
class ReviewForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.status = this.querySelector('[data-status]');
    this.submitButton = this.querySelector('button[type="submit"]');
    if (!this.form) return;
    this.onSubmit = this.onSubmit.bind(this);
    this.form.addEventListener('submit', this.onSubmit);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.onSubmit);
  }

  setStatus(message, isError) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.classList.toggle('text-critical', Boolean(isError));
    this.status.classList.toggle('text-on-surface', !isError);
  }

  async onSubmit(event) {
    event.preventDefault();
    const data = new FormData(this.form);
    if (!data.get('rating')) {
      this.setStatus(this.dataset.ratingMessage, true);
      this.form.querySelector('input[name="rating"]')?.focus();
      return;
    }

    this.submitButton?.setAttribute('disabled', '');
    this.setStatus('', false);
    try {
      const response = await fetch(this.dataset.endpoint, { method: 'POST', body: data });
      if (!response.ok) throw new Error(`Review submission failed: ${response.status}`);
      this.form.reset();
      this.form.hidden = true;
      this.setStatus(this.dataset.successMessage, false);
    } catch (error) {
      console.error(error);
      this.setStatus(this.dataset.errorMessage, true);
    } finally {
      this.submitButton?.removeAttribute('disabled');
    }
  }
}

if (!customElements.get('s-review-form')) {
  customElements.define('s-review-form', ReviewForm);
}
